import { Badge, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissionRoutes } from '@/hooks/usePermissionRoutes';
import { PageLink, PageLinkMenuKey } from '@/config/pageConfig';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteConfig } from '@/types/route';
import { getSelectLimitUserNum } from '@/api/user';
import { useRequest } from 'ahooks';

interface SiderMenuProps {
  /** 菜单是否折叠 */
  collapsed: boolean;
  /** 菜单点击回调 */
  onMenuClick?: () => void;
}

type MenuItem = Required<MenuProps>['items'][number];

/**
 * 将路由配置转换为菜单项
 * 递归处理所有层级的子菜单
 */
const convertRouteToMenuItem = (
  route: RouteConfig,
  index: number
): MenuItem | null => {
  // 如果路由被标记为隐藏，则不显示
  if (route.hideInMenu) {
    return null;
  }

  const key = route.path === '#' ? `${route.path}-${index}` : route.path;
  const baseMenuItem = {
    key: key,
    icon: route.icon,
    label: route.label,
  };

  // 如果没有子路由，直接返回基础菜单项
  if (!route.children?.length) {
    return baseMenuItem;
  }

  // 递归处理子路由
  const children = route.children
    .map(convertRouteToMenuItem)
    .filter((item): item is MenuItem => item !== null);

  // 如果没有可显示的子菜单，则只返回基础菜单项
  if (children.length === 0) {
    return baseMenuItem;
  }

  // 返回带有子菜单的菜单项
  return {
    ...baseMenuItem,
    children,
  };
};

/**
 * 递归查找路由的所有父级路径
 */
const findParentPaths = (
  path: string,
  items: MenuItem[],
  parentPath: string[] = []
): string[] => {
  for (const item of items) {
    if (!item) continue;

    if (item.key === path) {
      return parentPath;
    }

    if (item.children) {
      const found = findParentPaths(path, item.children, [
        ...parentPath,
        item.key as string,
      ]);
      if (found.length > 0) {
        return found;
      }
    }
  }

  return [];
};

/**
 * 侧边栏菜单组件
 */
export const SiderMenu: React.FC<SiderMenuProps> = ({
  collapsed,
  onMenuClick,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { routes: routesConfig } = usePermissionRoutes();

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const { data, run } = useRequest(async () => {
    const res = await getSelectLimitUserNum();
    if (res.status === 200) return res.data ?? 0;
    return 0;
  });

  const addBadgeToMenuItem = useCallback((item: MenuItem, data: number) => {
    if (item!.key === PageLink.UserMng) {
      if (data) {
        return {
          ...item,
          label: <Badge count={data} offset={[10, 0]}>
            <span className="menu-item-text">{item!.label}</span>
          </Badge>
        }
      }

      return item
    }

    const children = item.children;
    if (children?.length) {
      const newchildren = children.map(item => addBadgeToMenuItem(item, data));

      return {
        ...item,
        children: newchildren
      }
    }

    return item;
  }, [])

  // 转换路由配置为菜单项
  const menuItems = useMemo(() => {
    const menus = routesConfig
      .map(convertRouteToMenuItem)
      .filter((item): item is MenuItem => item !== null);
    if (!data) return menus;

    return menus.map(item => {
      return addBadgeToMenuItem(item, data);
    });
  }, [routesConfig, data]);

  useEffect(() => {
    const timer = setInterval(() => {
      run();
    }, 10000);

    return () => {
      clearInterval(timer);
    }
  }, []);

  // 处理菜单点击
  const handleMenuClick = useCallback(
    (key: string) => {
      onMenuClick?.();
      navigate(key);
      setOpenKeys([]);
    },
    [navigate, onMenuClick]
  );

  // 更新选中的菜单项和展开的父级菜单
  useEffect(() => {
    const menuKey = PageLinkMenuKey[location.pathname];
    if (menuKey) {
      setSelectedKeys([menuKey]);

      if (!collapsed) {
        // 找到当前路径的所有父级路径
        const parentPaths = findParentPaths(menuKey, menuItems);
        if (parentPaths.length > 0) {
          setOpenKeys(parentPaths);
        }
      }
    }
  }, [location.pathname, collapsed, menuItems]);

  // 处理菜单展开/收起
  const handleOpenChange = useCallback(
    (keys: string[]) => {
      // 只在非折叠状态下更新展开的菜单
      if (!collapsed) {
        setOpenKeys(keys);
      }
    },
    [collapsed]
  );

  return (
    <Menu
      theme="light"
      mode="inline"
      selectedKeys={selectedKeys}
      openKeys={collapsed ? [] : openKeys}
      onClick={(e) => handleMenuClick(e.key)}
      onOpenChange={handleOpenChange}
      items={menuItems}
      className='menu-wrap'
    />
  );
};
